import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ExtendedSelect from 'components/extendedSelect';
import settings from 'settings';
import {
  Form, Icon, Input, Tooltip
} from 'antd';
import { FormWrapper } from 'components/Page';
import { convertToSelect, getKey, getLabel } from 'library/helpers';
import _ from 'lodash';
import TagSelector from 'components/tagSelector/tag-selector';
import { THEME_NAMES } from 'selectors/tag.selectors';
// Redux
import { connect } from 'react-redux';
import { list } from 'actions';
import REDUCER_KEY from 'reducers/reducer.keys';

const FormItem = Form.Item;

const mapStateToProps = state => ({
  validators: state.api.validators.data.results
});

const mapDispatchToProps = dispatch => ({
  loadValidators(searchText) {
    const page = 0;
    const pageSize = settings.APP_CONFIG.dropListSize;
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [];

    if (!_.isEmpty(searchText)) {
      filters.push({
        id: 'name__icontains',
        value: searchText
      });
    }
    return dispatch(list(REDUCER_KEY.validators.key, {
      page,
      pageSize,
      filters,
      sorted
    }));
  }
});

const mapPropsToFields = (props) => {
  const { dataSource } = props;
  const { name, base_validator, ...rest } = dataSource;
  const restKeys = Object.keys(rest);
  const dynamicFields = {};
  for (let i = 0; i < restKeys.length; i++) {
    const potentialFieldValue = rest[restKeys[i]];
    if (typeof potentialFieldValue !== 'object') {
      dynamicFields[restKeys[i]] = Form.createFormField({ value: potentialFieldValue });
    }
  }

  return {
    ...dynamicFields,
    name: Form.createFormField({ value: name }),
    base_validator: Form.createFormField({ value: convertToSelect(base_validator) })
  };
};

const onFieldsChange = (props, fields) => {
  const { onFieldsChange: dispatchOnFieldsChange } = props;
  if (dispatchOnFieldsChange) {
    dispatchOnFieldsChange(fields);
  }
};

const onValuesChange = (props, values) => {
  const { onValuesChange: dispatchOnValuesChange } = props;
  if (dispatchOnValuesChange) {
    dispatchOnValuesChange(values);
  }
};

const getValidationRules = (param) => {
  const isRequired = !(param.config && param.config.required === false);
  if (param.param_type_name === 'Integer') {
    return {
      required: isRequired,
      pattern: /^\d+$/,
      message: `${param.display_name} must be a number`
    };
  }
  if (param.param_type_name === 'Time') {
    return {
      required: isRequired,
      pattern: /^\d{2}:\d{2}$/,
      message: `${param.display_name} must be a time (in the format HH:MM)`
    };
  }
  return {
    required: isRequired,
    message: `${param.display_name} is required`
  };
};
const getInput = (param) => {
  if (param.param_type_name === 'Tag') {
    return (
      <TagSelector
        sorting={[
          {
            id: 'collection__name',
            desc: false
          },
          {
            id: 'name',
            desc: false
          }
        ]}
        theme={THEME_NAMES[param.config.collection_name]}
        tagSelectorReducerKey={REDUCER_KEY[`${param.config.collection_name}CollectionValues`]}
        placeholder="Select a Tag"
        dropdownMatchSelectWidth={false}
        labelInValue={false}
        mode="default"
      />
    );
  }
  return <Input />;
};

const FormInput = ({
  canAdmin, dataSource, inputFormItemLayout, form, validators, loadValidators
}) => {
  const { getFieldDecorator } = form;
  const {
    id, name, base_validator, actual_parameters, ...rest
  } = dataSource;
  let validatorDescription = '';

  useEffect(() => {
    loadValidators();
  }, [id]);

  const isExisting = id > 0;

  let validatorParams = [];
  if (validators && base_validator) {
    const key = base_validator.key || base_validator.id;
    const potentialValidator = validators.find(v => v.id === key);
    if (potentialValidator) {
      validatorParams = potentialValidator.required_parameters;
      validatorDescription = potentialValidator.description;
    }
  }
  if (isExisting) {
    for (let i = 0; i < validatorParams.length; i++) {
      const v = validatorParams[i];
      if (Object.keys(rest)
        .indexOf(v.name) === -1) {
        const actual = actual_parameters.find(a => a.parameter === v.id);
        if (actual) {
          rest[v.name] = Number.isNaN(Number(actual.value)) ? actual.value : Number(actual.value);
        }
      }
    }
  }

  const readonly = !canAdmin;
  const ValidatorLabel = validatorDescription ? () => (
    <Tooltip title={validatorDescription} placement="bottomRight">
      <span>Base Validator </span>
      <Icon type="question-circle" />
    </Tooltip>
  ) : () => <span>Base Validator</span>;
  return (
    <Form>
      <FormItem {...inputFormItemLayout} label="Name">
        {getFieldDecorator('name', {
          initialValue: name,
          rules: [{
            required: true,
            message: 'Please enter a validation name.'
          }]
        })(<Input disabled={readonly} />)}
      </FormItem>
      <FormItem {...inputFormItemLayout} label={<ValidatorLabel />}>
        {getFieldDecorator('base_validator', {
          initialValue: base_validator,
          rules: [{
            required: true,
            message: 'Please enter a base validator.'
          }]
        })(
          <ExtendedSelect
            placeholder="Select Base Validator"
            dataSource={validators}
            optionKey={getKey}
            optionLabel={getLabel}
            disabled={readonly || isExisting}
            onFocus={loadValidators}
            onSearch={loadValidators}
          />
        )}
      </FormItem>
      {validatorParams.map(p => (
        <FormItem {...inputFormItemLayout} label={p.display_name} key={p.id}>
          {getFieldDecorator(p.name, {
            initialValue: rest[p.name],
            validateTrigger: ['onChange', 'onBlur'],
            rules: [{ required: true, ...getValidationRules(p) }]
          })(getInput(p))}
        </FormItem>
      ))}
    </Form>
  );
};

FormInput.propTypes = {
  canAdmin: PropTypes.bool,
  dataSource: PropTypes.object,
  form: PropTypes.object,
  inputFormItemLayout: PropTypes.object,
  validators: PropTypes.array,
  loadValidators: PropTypes.func,
  onFieldsChange: PropTypes.func,
  onValuesChange: PropTypes.func
};

FormInput.defaultProps = {
  canAdmin: false,
  dataSource: {},
  form: {},
  inputFormItemLayout: {},
  validators: [],
  loadValidators() {
  },
  onFieldsChange() {
  },
  onValuesChange() {
  }
};

const ConnectedFormInput = connect(mapStateToProps, mapDispatchToProps)(FormInput);

const ValidationInput = FormWrapper(Form.create({
  mapPropsToFields,
  onFieldsChange,
  onValuesChange
})(ConnectedFormInput));

export default ValidationInput;
